import Vue from 'vue'
import { applyRouteTitle } from '@/utils/title'

/**
 * 轻量级 history 路由（HTML5 History API，无 vue-router）
 *
 * URL 格式（真实路径，不带 #）:
 *   /                        -> 首页
 *   /taskblock               -> TaskBlock 子网站 - 首页/表格
 *   /taskblock/thoughts      -> TaskBlock 子网站 - 随想
 *   /taskblock/friends       -> TaskBlock 子网站 - 朋友
 *   /taskblock/profile       -> TaskBlock 子网站 - 个人中心（含「用户动态」）
 *   /admin                   -> 管理端（独立账号体系，子页在组件内切换）
 *
 * ⚠️ 管理端**不提供任何入口链接**：首页、导航、任务块页面里都不该出现指向它的按钮或文字，
 *    管理员靠手动输入 https://<域名>/admin 进入。这里只注册路由，不放入口 —— 加入口前先确认产品口径。
 *
 * 注：原「动态」子页（/taskblock/activity）已下线，动态能力迁入个人中心。
 *     ROUTES 里仍保留该路径，但指向个人中心，避免旧链接落到首页。
 *
 * ⚠️ **history 模式依赖服务端 SPA 回退**：/taskblock/thoughts 这类路径会被浏览器当成
 *    真实文件去请求，服务器找不到文件时必须回落到 index.html，否则「刷新即 404 白屏」。
 *    nginx 侧必须配 `try_files $uri $uri/ /index.html;`（完整片段见仓库根 README 的部署说明）。
 *
 * - 浏览器前进/后退触发 popstate；直接输入 URL / 刷新由当前 pathname 解析
 * - ⚠️ pushState / replaceState **不会**触发 popstate，所以 go() / replace() 里手动同步 state
 * - 兼容 hash 时代的旧链接（见 migrateLegacyHash）
 * - 暴露 Vue.observable 状态，组件可直接依赖
 */

// 部署 base：与 vue.config.js 的 publicPath 保持一致。
// 部署在域名根路径下留空字符串；若将来要部署到 /xxx 子路径，这里改成 '/xxx'，
// 并同步改 vue.config.js 的 publicPath 与 nginx 的 location（三处必须一致）。
const BASE = ''

// 一级页面到组件的映射
const PAGE_MAP = {
  home: 'HomePage',
  taskblock: 'TaskBlockPage',
  admin: 'AdminPage',
  // 隐私政策 / 用户协议：独立路由，sub 区分两份文档
  legal: 'LegalPage'
}

// taskblock 子网站下的二级页面
const SUB_MAP = {
  thoughts: 'ThoughtsPage',
  friends: 'FriendsPage',  // taskblock 内的 friends 子页
  profile: 'ProfilePage'
}

// 路径到 (page, sub) 的映射表
const ROUTES = {
  '/':                       { page: 'home',      sub: null },
  '/taskblock':              { page: 'taskblock', sub: null       },
  '/taskblock/table':        { page: 'taskblock', sub: null       },
  '/taskblock/thoughts':     { page: 'taskblock', sub: 'thoughts' },
  '/taskblock/friends':      { page: 'taskblock', sub: 'friends'  },
  '/taskblock/profile':      { page: 'taskblock', sub: 'profile' },
  // 动态页已下线，兼容旧链接：落到个人中心的「用户动态」
  '/taskblock/activity':     { page: 'taskblock', sub: 'profile' },
  // 管理端：独立账号体系。页面内不放入口链接，管理员手动输入 URL 直达
  '/admin':                  { page: 'admin', sub: null },
  // 隐私政策 / 用户协议：必须是独立可访问的 URL（注册页要能直接链过去，
  // 应用商店审核也要求能直接打开），所以不做成弹窗
  '/privacy':                { page: 'legal', sub: 'privacy' },
  '/terms':                  { page: 'legal', sub: 'terms' }
}

// 统一路径写法：保证以 / 开头、去掉结尾多余的 /（根路径除外）
function normalize(path) {
  let p = path || '/'
  if (!p.startsWith('/')) p = '/' + p
  if (p.length > 1) p = p.replace(/\/+$/, '') || '/'
  return p
}

// 当前真实路径（已剥掉部署 BASE 前缀）
function currentPath() {
  let path = window.location.pathname || '/'
  if (BASE && path.startsWith(BASE)) path = path.slice(BASE.length) || '/'
  return normalize(path)
}

// 解析路径 -> { page, sub }；未知路径回落到首页（不白屏）
function parsePath(rawPath) {
  const path = rawPath ? normalize(rawPath) : currentPath()
  return ROUTES[path] || { page: 'home', sub: null }
}

// 序列化为路径
function buildPath(page, sub) {
  if (sub) return '/' + page + '/' + sub
  if (page === 'home') return '/'
  return '/' + page
}

/**
 * 旧 hash 链接兼容。
 * 老链接形如 https://<域名>/#/taskblock/thoughts —— `#` 之后的内容不会发给服务器，
 * 所以服务端仍然返回首页；这里在启动时把它换成真实路径，避免老链接全部落到首页。
 * 只在「pathname 就是部署根」时迁移：hash 路由时代 pathname 恒为 /，
 * 所以这正是老链接的特征；pathname 已带路径时（新链接）一律不动。
 * 不认识的旧路径（如 #/about）只清掉 hash 落到首页，与 hash 时代的回落行为一致。
 */
function migrateLegacyHash() {
  const raw = (window.location.hash || '').replace(/^#/, '')
  // 只处理老式「路由 hash」（#/ 开头）；#section 这类页内锚点一律不动
  if (!raw.startsWith('/')) return false
  // hash 路由时代 pathname 恒为部署根；pathname 已带路径说明是新链接，不动
  if (currentPath() !== '/') return false
  const legacyPath = normalize(raw)
  // 认识的路径换成真实路径；不认识的（如 #/about）只清掉 hash，落到首页
  window.history.replaceState(null, '', BASE + (ROUTES[legacyPath] ? legacyPath : '/'))
  return true
}

// 先迁移老链接，再按最终路径初始化
migrateLegacyHash()

// 全局响应式状态
const state = Vue.observable({
  page: 'home',
  sub: null,
  fullPath: '/'
})

// 按当前 location 同步响应式状态（popstate / 启动 / 编程式跳转后都走这里）
function syncFromLocation() {
  const { page, sub } = parsePath()
  state.page = page
  state.sub = sub
  state.fullPath = buildPath(page, sub)
  // 每个路由同步专属网页标题（告别统一的 "Task Block"）
  applyRouteTitle(page, sub)
}

syncFromLocation()

// 浏览器前进/后退
window.addEventListener('popstate', syncFromLocation)

// 编程式跳转（新增历史记录）
function go(path) {
  const target = normalize(path)
  if (currentPath() === target) return
  window.history.pushState(null, '', BASE + target)
  // pushState 不触发 popstate，必须手动同步
  syncFromLocation()
}

// 编程式跳转（替换当前历史记录，用于重定向 / 兜底）
function replace(path) {
  const target = normalize(path)
  if (currentPath() !== target) {
    window.history.replaceState(null, '', BASE + target)
  }
  syncFromLocation()
}

// 兼容旧 setPage 风格的便捷方法
function setPage(page) {
  go(buildPath(page, null))
}

// 在 taskblock 子网站内部切换子页
function setSub(sub) {
  go(buildPath('taskblock', sub))
}

export default {
  state,
  go,
  replace,
  setPage,
  setSub,
  parsePath,
  buildPath,
  PAGE_MAP,
  SUB_MAP
}
